import React from 'react';
import {
    Box,
    Chip,
    Stack,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { getBookingStatusColor } from '../helpers/getBookingStatusColor';
import groupCommissionItems from '../helpers/groupCommissionItems';
import { useSort } from '../../../hooks/useSort';
import SortableHeaderCell from '../../../components/atom/SortableHeaderCell';

const GroupTable = ({
    group,
    showShowColumn,
    showGrandTotal,
    isLast,
    totalLineAmount,
    totalCommission,
}) => {
    const { t } = useTranslation();
    const sort = useSort(group.items, 'bookingId', 'asc');

    return (
        <Box key={group.commissionGroupName}>
            <Typography variant="h6" sx={{ mb: 1 }}>
                {group.commissionGroupName}
            </Typography>
            <TableContainer sx={{ maxHeight: '65vh' }}>
                <Table stickyHeader sx={{ width: '100%', '& thead th': { top: 0 } }}>
                    <TableHead>
                        <TableRow>
                            <SortableHeaderCell field="bookingId" sort={sort}>
                                {t('reports.commission.bookingId')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="bookingStatus" sort={sort}>
                                {t('reports.columns.bookingStatus')}
                            </SortableHeaderCell>
                            {showShowColumn && (
                                <SortableHeaderCell field="showTitle" sort={sort}>
                                    {t('reports.commission.showTitle')}
                                </SortableHeaderCell>
                            )}
                            <SortableHeaderCell field="agentName" sort={sort}>
                                {t('reports.commission.agentName')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="contact" sort={sort}>
                                {t('reports.commission.contact')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="customers" sort={sort}>
                                {t('reports.commission.customers')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="availability" sort={sort}>
                                {t('reports.commission.availability')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="lineTotal" sort={sort} align="right">
                                {t('reports.commission.lineTotal')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="commissionAmount" sort={sort} align="right">
                                {t('reports.commission.commissionAmount')}
                            </SortableHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sort.sortedItems.map((item) => (
                            <TableRow key={item.bookingId}>
                                <TableCell>{item.bookingId}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(`bookingstatus.${item.bookingStatus}`)}
                                        color={getBookingStatusColor(item.bookingStatus)}
                                        size="small"
                                    />
                                </TableCell>
                                {showShowColumn && <TableCell>{item.showTitle}</TableCell>}
                                <TableCell>{item.agentName}</TableCell>
                                <TableCell>
                                    {item.contact}
                                    <br />
                                    <Typography variant="caption" color="text.secondary">
                                        {item.email}
                                    </Typography>
                                </TableCell>
                                <TableCell>{item.customers}</TableCell>
                                <TableCell>{item.availability}</TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.lineTotal)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.commissionAmount)}
                                </TableCell>
                            </TableRow>
                        ))}

                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell colSpan={showShowColumn ? 7 : 6}>
                                <strong>{t('reports.summary.groupTotal', 'Subtotal')}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(group.lineTotal)}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(group.commissionAmount)}</strong>
                            </TableCell>
                        </TableRow>

                        {showGrandTotal && isLast && (
                            <TableRow sx={{ backgroundColor: 'action.selected' }}>
                                <TableCell colSpan={showShowColumn ? 7 : 6}>
                                    <strong>
                                        {t('reports.summary.grandTotal', 'Grand Total')}
                                    </strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>{formatCentsToUsd(totalLineAmount)}</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>{formatCentsToUsd(totalCommission)}</strong>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const CommissionReportTable = ({ items, showId, totalLineAmount, totalCommission }) => {
    const showShowColumn = !showId;
    const groups = groupCommissionItems(items);
    const showGrandTotal = groups.length > 1;

    return (
        <Stack spacing={3} sx={{ width: '100%' }}>
            {groups.map((group, groupIndex) => (
                <GroupTable
                    key={group.commissionGroupName}
                    group={group}
                    showShowColumn={showShowColumn}
                    showGrandTotal={showGrandTotal}
                    isLast={groupIndex === groups.length - 1}
                    totalLineAmount={totalLineAmount}
                    totalCommission={totalCommission}
                />
            ))}
        </Stack>
    );
};

export default CommissionReportTable;
